// ─────────────────────────────────────────────────────────────
// CSV IMPORT DEDUP
//
// Given a list of parsed CSV trades and the list of trades already
// in the account, determine which are new and which are duplicates.
//
// Strategy (in order of preference):
//   1. Broker trade ID match (most reliable — both sides have a real ID)
//   2. Fingerprint match (used when one side lacks broker_trade_id)
//
// Fingerprint = trade_date + symbol + entry_time + exit_time +
//               entry_price + exit_price + position_size + direction
// ─────────────────────────────────────────────────────────────

// Build a normalised key from a trade's attributes. Both CSV-parsed
// trades and database trades should produce the same key for the
// same actual trade.
export function fingerprintTrade(t) {
  if (!t) return "";
  const parts = [
    String(t.trade_date    || "").slice(0, 10),
    String(t.symbol        || "").toUpperCase().replace(/\s+/g, ""),
    String(t.entry_time    || "").slice(0, 5), // HH:MM (minutes precision)
    String(t.exit_time     || "").slice(0, 5), // HH:MM — differentiates same-minute entries
    Number(t.entry_price   || 0).toFixed(4),
    Number(t.exit_price    || 0).toFixed(4),
    Number(t.position_size || 0).toFixed(0),
    String(t.direction     || "").toLowerCase(),
  ];
  return parts.join("|");
}

// Compare a list of incoming (CSV-parsed) trades against the existing
// trades in the database. Returns { newTrades, duplicates }.
export function partitionTrades(incoming, existing) {
  // Build fast lookup sets from existing trades:
  // - by broker_trade_id (only for those that have one)
  // - by fingerprint (always populated)
  const existingIds         = new Set();
  const existingFingerprints = new Set();

  for (const t of existing || []) {
    if (t.broker_trade_id) {
      existingIds.add(String(t.broker_trade_id));
    }
    existingFingerprints.add(fingerprintTrade(t));
  }

  const newTrades  = [];
  const duplicates = [];

  for (const t of incoming || []) {
    let isDup = false;

    if (t.broker_trade_id && existingIds.has(String(t.broker_trade_id))) {
      isDup = true;
    } else if (existingFingerprints.has(fingerprintTrade(t))) {
      isDup = true;
    }

    if (isDup) duplicates.push(t);
    else       newTrades.push(t);
  }

  return { newTrades, duplicates };
}
